// CalorieBar.js
import React, { useEffect, useState } from 'react';
import { Row, Col, Typography, Spin } from 'antd';
import axios from 'axios';

const { Text } = Typography;

const fmtInt = (num) => Math.round(num).toLocaleString('en-CA');

const CalorieBar = ({ refreshSignal }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/calorie-tracking`,
        { withCredentials: true }
      );

      const item = res.data?.[0];
      if (!item) {
        setData(null);
        return;
      }

      const bmr = Number(item.bmr || 0);
      const tdee = Number(item.tdee || 0);
      const activity = Number(item.activity || 0);
      const deficit = Number(item.deficit || 0);
      const consumedToday = Number(item.consumedToday || 0);

      const available = bmr + tdee + activity - deficit;

      setData({
        consumed: consumedToday,
        available,
        activity,
      });
    } catch (err) {
      console.error('Error loading calorie tracking', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [refreshSignal]);

  if (loading || !data || data.available <= 0) {
    return (
      <div style={{ width: '100%', padding: 16, textAlign: 'center' }}>
        {loading ? <Spin /> : null}
      </div>
    );
  }

  const { consumed, available, activity } = data;

  const remaining = available - consumed;
  const overAmount = Math.max(consumed - available, 0);

  const baseLimit = 0.9 * available;

  // amounts *within available only*
  const greenAmount = Math.min(consumed, baseLimit, available);
  const yellowAmount =
    consumed > baseLimit
      ? Math.min(consumed, available) - baseLimit
      : 0;

  // widths as % of AVAILABLE (bar span)
  const greenWidth = (greenAmount / available) * 100;
  const yellowWidth = (yellowAmount / available) * 100;

  const activityWidth =
    activity > 0 ? (activity / available) * 100 : 0;

  const showConsumedLabel = greenWidth > 18;
  const showActivityLabel = activityWidth > 18;
  const showOverLabel = overAmount > 0; // red is outside bar

const totalShown = consumed; // or consumed + 0 if you only want bar+over to match consumed
const mainBarFlex = available / (totalShown || 1);
const overFlex = overAmount / (totalShown || 1);

  // boundary at available (end of yellow / bar right edge)
  const boundaryWidth = greenWidth + yellowWidth; // percent of bar

  const labelStyle = {
    fontSize: 12,
    lineHeight: 1.2,
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 900,
        margin: '0 auto',
        padding: '8px 0',
      }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 4 }}>
        <Col>
          <Text strong>Calories</Text>
        </Col>
      </Row>

      <div
  style={{
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  }}
>
       {/* Main bar: flex proportional to available */}
  <div
    style={{
      flex: mainBarFlex,
      height: 40,
      borderRadius: '4px 0 0 4px',
      background: '#f5f5f5',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
    }}
  >

          {/* Activity allowance band pinned to right (under green/yellow) */}
          {activity > 0 && activityWidth > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: `${100 - activityWidth}%`,
                height: '100%',
                width: `${activityWidth}%`,
                background: '#e6f4ff',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {showActivityLabel && (
                <div style={labelStyle}>
                  <Text style={{ color: '#1677ff' }}>Activity</Text>
                  <Text style={{ color: '#1677ff' }}>
                    {fmtInt(activity)} Cal
                  </Text>
                </div>
              )}
            </div>
          )}

          {/* Green – up to 90% of available */}
          {greenWidth > 0 && (
            <div
              style={{
                flexBasis: `${greenWidth}%`,
                background: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: 8,
                whiteSpace: 'nowrap',
                zIndex: 2,
              }}
            >
              {showConsumedLabel && (
                <div style={labelStyle}>
                  <Text style={{ color: '#fff' }}>Consumed</Text>
                  <Text style={{ color: '#fff' }}>
                    {fmtInt(consumed)} Cal
                  </Text>
                </div>
              )}
            </div>
          )}

          {/* Yellow – 90% to 100% of available */}
          {yellowWidth > 0 && (
            <div
              style={{
                flexBasis: `${yellowWidth}%`,
                background: '#faad14',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
              }}
            />
          )}

          {/* Remaining unused area inside available */}
          {greenWidth + yellowWidth < 100 && (
            <div
              style={{
                flexBasis: `${100 - greenWidth - yellowWidth}%`,
                background: 'transparent',
              }}
            />
          )}

          {/* Black marker at available (bar right edge) */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '100%',
              transform: 'translateX(-2px)',
              width: 4,
              height: '100%',
              background: '#000',
              zIndex: 3,
            }}
          />
        </div>

        {/* Red segment: overage OUTSIDE the bar, to the right */}
  {overAmount > 0 && (
    <div
      style={{
        flex: overFlex,
        height: 40,
        borderRadius: '0 4px 4px 0',
        background: '#ff4d4f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        padding: '0 12px',
      }}
    >
      <div style={labelStyle}>
        <Text style={{ color: '#fff' }}>Over</Text>
        <Text style={{ color: '#fff' }}>{fmtInt(overAmount)} Cal</Text>
      </div>
    </div>
  )}


        {/* Available label only when not over */}
        {overAmount <= 0 && (
          <div
            style={{
              marginLeft: 8,
              whiteSpace: 'nowrap',
            }}
          >
            <div style={labelStyle}>
              <Text>Available</Text>
              <Text>{fmtInt(remaining)} Cal</Text>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalorieBar;
